(() => {
  const root = document.querySelector('section.state-vaccine-finder-map');
  if (!root) return;

  const paths = Array.from(root.querySelectorAll('path.state-vaccine-finder-map__state'));
  const labels = Array.from(root.querySelectorAll('text.state-vaccine-finder-map__label'));
  const legendButtons = Array.from(
    root.querySelectorAll('button.state-vaccine-finder-map__legend-button'),
  );
  const searchInput = root.querySelector('input.state-vaccine-finder-map__search');
  const panelAbbr = root.querySelector('.state-vaccine-finder-map__panel-abbr');
  const panelName = root.querySelector('.state-vaccine-finder-map__panel-name');
  const panelNote = root.querySelector('.state-vaccine-finder-map__panel-note');
  const resourceLink = root.querySelector('a.state-vaccine-finder-map__resource');
  const resourceHost = root.querySelector('.state-vaccine-finder-map__resource-host');

  const EXEMPTION_KEYS = ['medical', 'religious', 'philosophical'];
  const STATUS_AVAILABLE = 'state-vaccine-finder-map__panel-status--available';
  const STATUS_UNAVAILABLE = 'state-vaccine-finder-map__panel-status--unavailable';

  const statusSpans = {};
  EXEMPTION_KEYS.forEach((key) => {
    const item = root.querySelector(
      `li.state-vaccine-finder-map__panel-item[data-exemption="${key}"]`,
    );
    statusSpans[key] = item && item.querySelector('.state-vaccine-finder-map__panel-status');
  });

  const namesByAbbr = new Map();
  paths.forEach((path) => {
    namesByAbbr.set(path.dataset.abbr, path.dataset.name || '');
  });

  let selectedAbbr = null;
  let activeTier = null;
  let query = '';

  /**
   * Read a state's data attributes into a plain object.
   *
   * @param {SVGPathElement} path The state path element.
   * @returns {Object} Abbreviation, name, tier, exemption flags, note, and resource data.
   */
  function getStateFromPath(path) {
    return {
      abbr: path.dataset.abbr || '',
      name: path.dataset.name || '',
      tier: path.dataset.tier || '',
      medical: path.dataset.medical === '1',
      religious: path.dataset.religious === '1',
      philosophical: path.dataset.philosophical === '1',
      note: path.dataset.note || '',
      resourceUrl: path.dataset.resourceUrl || '',
      resourceHost: path.dataset.resourceHost || '',
    };
  }

  /**
   * Render the detail panel from a state path's data attributes.
   *
   * @param {SVGPathElement} path The selected state path element.
   * @returns {void}
   */
  function updatePanel(path) {
    const state = getStateFromPath(path);

    if (panelAbbr) panelAbbr.textContent = state.abbr;
    if (panelName) panelName.textContent = state.name;

    EXEMPTION_KEYS.forEach((key) => {
      const status = statusSpans[key];
      if (!status) return;
      const available = state[key];
      status.textContent = available ? 'Available' : 'Not available';
      status.classList.toggle(STATUS_AVAILABLE, available);
      status.classList.toggle(STATUS_UNAVAILABLE, !available);
    });

    if (panelNote) {
      panelNote.textContent = state.note;
      panelNote.classList.toggle('is-hidden', state.note === '');
    }

    if (resourceLink) {
      if (state.resourceUrl === '') {
        resourceLink.classList.add('is-hidden');
      } else {
        resourceLink.setAttribute('href', state.resourceUrl);
        if (resourceHost) resourceHost.textContent = state.resourceHost;
        resourceLink.classList.remove('is-hidden');
      }
    }
  }

  /**
   * Determine whether a state matches the current search query.
   *
   * @param {string} abbr The state's two-letter abbreviation.
   * @returns {boolean} True when the state name contains, or its abbreviation equals, the query.
   */
  function matchesQuery(abbr) {
    const name = namesByAbbr.get(abbr) || '';
    return name.toLowerCase().includes(query) || abbr.toLowerCase() === query;
  }

  /**
   * Decide whether a map element is filtered out by the active tier or search query.
   *
   * @param {SVGElement} el A state path or label carrying abbr and tier data attributes.
   * @returns {boolean} True when the element should be dimmed.
   */
  function isDimmed(el) {
    const tierMismatch = activeTier !== null && el.dataset.tier !== activeTier;
    const queryMismatch = query !== '' && !matchesQuery(el.dataset.abbr || '');
    return tierMismatch || queryMismatch;
  }

  /**
   * Re-apply the tier and search filters to every state path and map label.
   *
   * @returns {void}
   */
  function applyFilters() {
    paths.forEach((path) => path.classList.toggle('dimmed', isDimmed(path)));
    labels.forEach((label) => label.classList.toggle('dimmed', isDimmed(label)));
  }

  /**
   * Select a state path and refresh the detail panel.
   *
   * @param {SVGPathElement} path The state path element to select.
   * @returns {void}
   */
  function selectState(path) {
    const previous = paths.find((p) => p.dataset.abbr === selectedAbbr);
    if (previous) previous.classList.remove('active');
    path.classList.add('active');
    selectedAbbr = path.dataset.abbr || null;
    updatePanel(path);
  }

  /**
   * Toggle the isolated exemption tier and sync the legend buttons.
   *
   * @param {string} tier The tier to isolate: "medical", "religious", or "philosophical".
   * @returns {void}
   */
  function setActiveTier(tier) {
    activeTier = activeTier === tier ? null : tier;
    legendButtons.forEach((button) => {
      const isActive = button.dataset.tier === activeTier;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.classList.toggle('active', isActive);
    });
    applyFilters();
  }

  /**
   * Handle a click on a state path.
   *
   * @param {MouseEvent} event The click event.
   * @returns {void}
   */
  function handleStateClick(event) {
    selectState(event.currentTarget);
  }

  /**
   * Handle Enter or Space activation on a focused state path.
   *
   * @param {KeyboardEvent} event The keydown event.
   * @returns {void}
   */
  function handleStateKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    selectState(event.currentTarget);
  }

  /**
   * Handle a click on a legend tier button.
   *
   * @param {MouseEvent} event The click event.
   * @returns {void}
   */
  function handleLegendClick(event) {
    setActiveTier(event.currentTarget.dataset.tier);
  }

  /**
   * Handle input in the state search field.
   *
   * @param {Event} event The input event.
   * @returns {void}
   */
  function handleSearchInput(event) {
    query = event.currentTarget.value.trim().toLowerCase();
    applyFilters();
  }

  /**
   * Seed the initial selection and attach all map interaction listeners.
   *
   * @returns {void}
   */
  function init() {
    if (!panelAbbr || !panelName) return;

    const initial = paths.find((path) => path.classList.contains('active'));
    if (initial) selectedAbbr = initial.dataset.abbr || null;

    paths.forEach((path) => {
      path.addEventListener('click', handleStateClick);
      path.addEventListener('keydown', handleStateKeydown);
    });

    legendButtons.forEach((button) => {
      button.addEventListener('click', handleLegendClick);
    });

    if (searchInput) searchInput.addEventListener('input', handleSearchInput);
  }

  init();
})();
